import {
  EMPTY,
  FLAG,
  HIDDEN,
  MAX_SIZE,
  SAFE,
  type MinesweeperDefinition,
  type MinesweeperProgress,
  type MinesweeperSubmission,
} from "./types";

const DEFAULT_RULES = {
  en: "Flag every mine. A number tells how many mines touch its cell, diagonals included; the total number of mines is given. Every mine can be found by reasoning, without guessing.",
  fr: "Marquez chaque mine d'un drapeau. Un nombre indique combien de mines touchent sa case, diagonales comprises ; le nombre total de mines est donné. Chaque mine se trouve par raisonnement, sans jamais deviner.",
};

export function emptyDefinition(): MinesweeperDefinition {
  return {
    puzzleType: "minesweeper",
    typeVersion: 1,
    rows: 8,
    cols: 8,
    cells: blankRows(8, 8),
    mines: 0,
    rulesText: { ...DEFAULT_RULES },
  };
}

export function clampSize(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(MAX_SIZE, Math.max(1, Math.trunc(value)));
}

export function blankRows(rows: number, cols: number): string[] {
  return Array.from({ length: rows }, () => EMPTY.repeat(cols));
}

/** Crops or pads a row set to the given size, keeping the top-left corner. */
export function resizeRows(source: string[], rows: number, cols: number): string[] {
  return Array.from({ length: rows }, (_, r) => {
    const row = source[r] ?? "";
    return row.length >= cols ? row.slice(0, cols) : row + EMPTY.repeat(cols - row.length);
  });
}

function sanitizeRow(raw: unknown, allowed: readonly string[]): string {
  if (typeof raw !== "string") return "";
  return Array.from(raw, (char) => (allowed.includes(char) ? char : EMPTY)).join("");
}

/** Marks never sit on a revealed cell: whatever a stored blob says there is dropped. */
export function clearRevealed(marks: string[], cells: string[]): string[] {
  return marks.map((row, r) =>
    Array.from(row, (char, c) => ((cells[r]?.[c] ?? HIDDEN) === HIDDEN ? char : EMPTY)).join(""),
  );
}

export function emptyProgress(definition: MinesweeperDefinition): MinesweeperProgress {
  return { version: 1, rows: blankRows(definition.rows, definition.cols) };
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: MinesweeperDefinition,
  raw: unknown,
): MinesweeperProgress {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw))
    return emptyProgress(definition);
  const source = (raw as { rows?: unknown }).rows;
  if (!Array.isArray(source)) return emptyProgress(definition);
  const rows = source.map((row) => sanitizeRow(row, [FLAG, SAFE, EMPTY]));
  return {
    version: 1,
    rows: clearRevealed(resizeRows(rows, definition.rows, definition.cols), definition.cells),
  };
}

/** The server only reads flags: safe marks are the player's own notes. */
export function serializeSubmission(
  definition: MinesweeperDefinition,
  progress: MinesweeperProgress,
): MinesweeperSubmission {
  return {
    rows: clearRevealed(
      resizeRows(progress.rows, definition.rows, definition.cols),
      definition.cells,
    ).map((row) => row.replaceAll(SAFE, EMPTY)),
  };
}
